// Card-timer commands: start a task, pause, resume, point the running timer elsewhere. Pure.
import { makeTimerState, computeCardActiveTime, bankable } from '../timer.js';
import { localDateStr } from '../time.js';
import { timeEditRecord } from '../timeledger.js';
import { set, update } from './writes.js';
import { buildTimeEntry, stopTask, sessionTask } from './tasks.js';
import { autoDoingWrites } from '../boards.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

/**
 * Start timing a task (the phone's startCard): whatever the timer holds on its current task is
 * banked first (bankable() decides — on the clock, this session, under the cap), then a fresh timer.
 */
export function startTask(ctx, { taskId, lock = false }) {
  const task = (ctx.tasks || []).find((t) => t.id === taskId);
  if (!task) throw new Error(`no task ${taskId}`);
  if (task.status === 'done') throw new Error(`${task.title} is done — reopen it first`);
  const at = now(ctx);
  const timer = ctx.timer;
  const writes = [];
  let banked = 0;
  if (timer?.taskId) {
    const b = bankable({ timer, clockedIn: ctx.clock, taskId: timer.taskId, now: at });
    if (b.elapsed > 0 && (ctx.tasks || []).some((t) => t.id === timer.taskId)) {
      writes.push(...stopTask(ctx, { id: timer.taskId, elapsed: b.elapsed, sessionId: ctx.clock?.sessionId || null, timeEntry: buildTimeEntry(b.elapsed, at, ctx.newId), keepTimer: true, keepLock: true }).writes);
      banked = b.elapsed;
    }
  }
  const t = makeTimerState({ taskId, startedAt: at });
  writes.push(set('meta/timer', t), ...sessionTask(ctx, { currentTaskId: taskId, currentTaskAt: at, currentPause: null }));
  if (lock) writes.push(set('meta/lock', { active: true, taskId }));
  writes.push(...autoDoingWrites(task, at));   // boards: a started card moves to Doing (from Backlog / Next / no stage)
  return { writes, timer: t, banked, bankedFrom: banked ? timer.taskId : null, clockedIn: !!ctx.clock?.active };
}

export function pauseTask(ctx, { type = 'break', context = null } = {}) {
  const timer = ctx.timer;
  if (!timer?.startedAt) throw new Error('no timer running — startTask first');
  if (timer.pauseType) throw new Error(`already paused (${timer.pauseType}) — resume first`);
  const t = makeTimerState({ taskId: timer.taskId, startedAt: timer.startedAt, pauseType: type, pauseStartedAt: now(ctx), pauseContext: context, accumulatedPause: timer.accumulatedPause || 0 });
  return { writes: [set('meta/timer', t), ...sessionTask(ctx, { currentPause: type })], timer: t };
}

/** Resume: the pause is logged on the task (statusLog + pauseTime) and folded into the timer. */
export function resumeTask(ctx) {
  const timer = ctx.timer;
  if (!timer?.pauseType || !timer.pauseStartedAt) throw new Error('not paused');
  const at = now(ctx);
  const pauseDuration = at - timer.pauseStartedAt;
  const writes = [];
  const task = timer.taskId ? (ctx.tasks || []).find((t) => t.id === timer.taskId) : null;
  if (task) writes.push(update(`tasks/${task.id}`, { statusLog: [...(task.statusLog || []), { type: timer.pauseType, startedAt: timer.pauseStartedAt, endedAt: at, duration: pauseDuration, context: timer.pauseContext || null }], pauseTime: (task.pauseTime || 0) + pauseDuration }));
  const t = makeTimerState({ taskId: timer.taskId, startedAt: timer.startedAt, accumulatedPause: (timer.accumulatedPause || 0) + pauseDuration });
  writes.push(set('meta/timer', t), ...sessionTask(ctx, { currentPause: null }));
  return { writes, timer: t, pauseMs: pauseDuration };
}

/** Stop the running task's timer: bank its time, fresh timer, lock off. */
export function stopTimer(ctx) {
  const timer = ctx.timer;
  if (!timer?.taskId) throw new Error('no task on the timer');
  const at = now(ctx);
  const b = bankable({ timer, clockedIn: ctx.clock, taskId: timer.taskId, now: at });
  const timeEntry = b.elapsed > 0 ? buildTimeEntry(b.elapsed, at, ctx.newId) : null;
  const r = stopTask(ctx, { id: timer.taskId, elapsed: b.elapsed, sessionId: ctx.clock?.sessionId || null, timeEntry });
  return { writes: r.writes, banked: b.elapsed, taskId: timer.taskId, unbankedMs: b.elapsed ? 0 : computeCardActiveTime(timer, at) };
}

/** The timer was running on the wrong task: point it at the right one WITHOUT banking; the ledger records it. */
export function retargetTimer(ctx, { taskId }) {
  const timer = ctx.timer;
  if (!timer?.startedAt) throw new Error('no timer running');
  const to = (ctx.tasks || []).find((t) => t.id === taskId);
  if (!to) throw new Error(`no task ${taskId}`);
  const from = timer.taskId ? (ctx.tasks || []).find((t) => t.id === timer.taskId) || null : null;
  const t = { ...timer, taskId };
  const at = now(ctx);
  const rec = timeEditRecord({ id: ctx.newId(), kind: 'retarget', task: from, toTask: to, by: ctx.by || null, now: at, before: { date: localDateStr(timer.startedAt), startTime: hhmm(timer.startedAt), endTime: null, duration: 0 } });
  return { writes: [set('meta/timer', t), set(`timeEdits/${rec.id}`, rec), ...sessionTask(ctx, { currentTaskId: taskId })], timer: t };
}
